import React, { useMemo, useCallback, useContext, createContext } from 'react';
import fetchHelper from '../Helpers/fetchHelper';
import consoleLogDev from '../Helpers/loggingHelper';
import PropTypes from 'prop-types';
import { SENTRY_GLOBAL_COMPONENT, SENTRY_EVENTS } from '../consts/sentryEvents';
import useEventEmitter from '../Helpers/useEventEmitter';

const StorageContext = createContext(null);
export function StorageAPIProvider(props) {
    const { storageKey, children } = props;
    const applyEmitterHandler = useEventEmitter(SENTRY_GLOBAL_COMPONENT);

    const retrievalUrl = `/api/dashboard/storage/${storageKey}`;

    const retrieveData = useCallback(async () => {
        try {
            const response = await fetchHelper(retrievalUrl, { method: 'GET' });

            return await response.json();
        }
        catch (error) {
            applyEmitterHandler((emitter) => {
                emitter.emit(SENTRY_EVENTS.ON_SENTRY_ERROR, error);
            });
            console.error(error + ` for: '${retrievalUrl}'`);
        }
    }, [retrievalUrl, applyEmitterHandler]);

    const storeData = useCallback(async (dataToStore) => {
        try {
            
            let dataToSend = dataToStore;

            // parse string to JSON
            if ( typeof dataToStore != 'string' ) {
                try {
                    dataToSend = JSON.stringify( dataToStore );
                } catch( error ) {
                    applyEmitterHandler((emitter) => {
                        emitter.emit(SENTRY_EVENTS.ON_SENTRY_ERROR, error);
                    });
                    console.error( 'JSON stringify error: ' + error );
                    return;
                }
            }

            const response = await fetchHelper(retrievalUrl, { method: 'POST', body: JSON.stringify({ content: dataToSend }) });

            return response.ok;
        }
        catch (error) {
            applyEmitterHandler((emitter) => {
                emitter.emit(SENTRY_EVENTS.ON_SENTRY_ERROR, error);
            });
            console.error(error + ` for: '${retrievalUrl}'`);
        }
    }, [retrievalUrl, applyEmitterHandler]);

    const contextValue = useMemo(
        () => ({
            retrieveData,
            storeData
        }), [retrieveData, storeData]);

    return (
        <StorageContext.Provider value={contextValue}>
            {children}
        </StorageContext.Provider>
    )
}

StorageAPIProvider.propTypes = {
    storageKey: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};

export const StorageLocalProvider = (props) => {
    const { storageKey, children } = props;
    const applyEmitterHandler = useEventEmitter(SENTRY_GLOBAL_COMPONENT);

    const retrieveData = useCallback(async () => {
        let data = localStorage.getItem(storageKey);

        consoleLogDev(`[StorageProvider] retrieve ${storageKey}:`, data);

        return JSON.parse(data);
    }, [storageKey]);

    const storeData = useCallback(async (dataToStore) => {
        let dataToSend = dataToStore;

        // parse string to JSON
        if ( typeof dataToStore != 'string' ) {
            try {
                dataToSend = JSON.stringify( dataToStore );
            } catch( error ) {
                applyEmitterHandler((emitter) => {
                    emitter.emit(SENTRY_EVENTS.ON_SENTRY_ERROR, error);
                });
                console.error( 'JSON stringify error: ' + error );
                return;
            }
        }

        localStorage.setItem(storageKey, dataToSend);

        return true;
    }, [storageKey, applyEmitterHandler]);

    const contextValue = useMemo(
        () => ({
            retrieveData,
            storeData
        }), [retrieveData, storeData]);

    return (
        <StorageContext.Provider value={contextValue}>
            {children}
        </StorageContext.Provider>
    )
}

StorageLocalProvider.propTypes = {
    storageKey: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};

export default function useStorage() {
    const { retrieveData, storeData } = useContext(StorageContext);

    return useMemo(
        () => ({
            retrieveData: async () => retrieveData(),
            storeData: async (dataToStore) => { return storeData(dataToStore); }
        }), 
        [retrieveData, storeData]
    );
}